<script setup lang="ts">
import type { TutorBillingRow } from '../model/interfaces'

import { faCheck, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { AppHoverCard } from '@shared/ui/tooltips'
import { AppIcon } from '@shared/ui/icons'

import { useTutorBillingStatusFlags } from '../model/tutor-billing-status-flags.hook'

defineOptions({
  name: 'TutorBillingStatusFlags',
})

const props = defineProps<{
  row: TutorBillingRow
}>()

const {
  dots,
  hasIssues,
  cardRows,
  triggerLabel,
} = useTutorBillingStatusFlags({
  row: computed(() => props.row),
})
</script>

<template>
  <div class="tutor-billing-status-flags">
    <AppHoverCard placement="bottom-start">
      <span
        class="tutor-billing-status-flags__trigger"
        role="button"
        tabindex="0"
        :aria-label="triggerLabel"
      >
        <span
          v-for="dot in dots"
          :key="dot.flag"
          :class="[
            'tutor-billing-status-flags__dot',
            dot.isOn ? 'tutor-billing-status-flags__dot--on' : 'tutor-billing-status-flags__dot--off',
          ]"
          :title="dot.label"
        />
        <AppIcon
          v-if="hasIssues"
          class="tutor-billing-status-flags__issue"
          :icon="faTriangleExclamation"
          size="small"
          color="red-500"
        />
      </span>

      <template #content>
        <div class="tutor-billing-status-flags__card">
          <div
            v-for="cardRow in cardRows"
            :key="cardRow.flag"
            class="tutor-billing-status-flags__card-row"
          >
            <AppText
              name="body2"
              color="slate-900"
            >
              {{ cardRow.label }}
            </AppText>
            <span class="tutor-billing-status-flags__sr-only">{{ cardRow.stateText }}</span>
            <template v-if="cardRow.isOn">
              <AppIcon
                v-if="cardRow.isIssue"
                :icon="faTriangleExclamation"
                size="small"
                color="red-500"
              />
              <AppIcon
                v-else
                :icon="faCheck"
                size="small"
                color="emerald-700"
              />
            </template>
          </div>
        </div>
      </template>
    </AppHoverCard>
  </div>
</template>

<style scoped lang="scss" src="./TutorBillingStatusFlags.scss" />
